import type {
  CatalogMenuSnapshot,
  CatalogRuntimePayload,
  ModifierPriceRuntimePayload,
  ModifierPriceSnapshot,
  PriceCursor,
  PriceRuntimePayload,
  PriceSnapshot,
  RuntimeCursor,
} from "../gateway/catalog-price-client";
import type { TrustedTerminalIdentity } from "./terminal-state";
import { posRuntimeDb } from "./pos-db";

const catalogDomain = "CATALOG_MENU";
const priceDomain = "SELLABLE_PRICE";
const modifierPriceDomain = "MODIFIER_PRICE";

export type LocalCatalogMenu = {
  menuItemId: string;
  revision: number;
  snapshot: CatalogMenuSnapshot;
};

export type LocalSellablePrice = {
  menuItemId: string;
  menuVariantId: string | null;
  snapshot: PriceSnapshot;
};

export type LocalModifierPrice = {
  modifierOptionId: string;
  snapshot: ModifierPriceSnapshot;
};

export type LocalCatalogPriceRuntime = {
  catalogRevision: number;
  priceCursor: PriceCursor | null;
  modifierPriceCursor: RuntimeCursor | null;
  menus: LocalCatalogMenu[];
  prices: LocalSellablePrice[];
  modifierPrices: LocalModifierPrice[];
};

function catalogScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:catalog`;
}

function priceScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:channel:${identity.commerce_channel_id}:price`;
}

function modifierPriceScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:channel:${identity.commerce_channel_id}:modifier-price`;
}

function catalogCursorKey(identity: TrustedTerminalIdentity) {
  return `catalog:cursor:${catalogScope(identity)}`;
}

function priceCursorKey(identity: TrustedTerminalIdentity) {
  return `price:cursor:${priceScope(identity)}`;
}

function modifierPriceCursorKey(identity: TrustedTerminalIdentity) {
  return `modifier-price:cursor:${modifierPriceScope(identity)}`;
}

function catalogProjectionKey(identity: TrustedTerminalIdentity, menuItemId: string) {
  return `catalog:${catalogScope(identity)}:menu:${menuItemId}`;
}

function priceProjectionKey(
  identity: TrustedTerminalIdentity,
  menuItemId: string,
  menuVariantId: string | null,
) {
  return `price:${priceScope(identity)}:menu:${menuItemId}:variant:${menuVariantId ?? "BASE"}`;
}

function modifierPriceProjectionKey(identity: TrustedTerminalIdentity, modifierOptionId: string) {
  return `modifier-price:${modifierPriceScope(identity)}:option:${modifierOptionId}`;
}

function parseCatalogCursor(value: unknown) {
  if (!value || typeof value !== "object") return 0;
  const revision = (value as { revision?: unknown }).revision;
  return typeof revision === "number" && Number.isSafeInteger(revision) && revision >= 0
    ? revision
    : 0;
}

function parseRuntimeCursor(value: unknown): RuntimeCursor | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<RuntimeCursor>;
  if (
    typeof candidate.effective_at !== "string" ||
    !candidate.effective_at ||
    typeof candidate.revision !== "number" ||
    !Number.isSafeInteger(candidate.revision) ||
    candidate.revision < 0
  ) {
    return null;
  }
  return { effective_at: candidate.effective_at, revision: candidate.revision };
}

export async function readCatalogPriceRuntime(
  identity: TrustedTerminalIdentity,
): Promise<LocalCatalogPriceRuntime> {
  const cScope = catalogScope(identity);
  const pScope = priceScope(identity);
  const mScope = modifierPriceScope(identity);
  const [catalogMeta, priceMeta, modifierPriceMeta, catalogRows, priceRows, modifierPriceRows] = await Promise.all([
    posRuntimeDb.runtimeMeta.get(catalogCursorKey(identity)),
    posRuntimeDb.runtimeMeta.get(priceCursorKey(identity)),
    posRuntimeDb.runtimeMeta.get(modifierPriceCursorKey(identity)),
    posRuntimeDb.projections.where("scopeKey").equals(cScope).toArray(),
    posRuntimeDb.projections.where("scopeKey").equals(pScope).toArray(),
    posRuntimeDb.projections.where("scopeKey").equals(mScope).toArray(),
  ]);

  const menus = catalogRows
    .filter((row) => row.domain === catalogDomain && row.payload && typeof row.payload === "object")
    .map((row) => ({
      menuItemId: row.key.slice(row.key.lastIndexOf(":menu:") + 6),
      revision: Number(row.revision) || 0,
      snapshot: row.payload as CatalogMenuSnapshot,
    }))
    .filter((row) => Boolean(row.menuItemId && row.snapshot?.menu?.name));

  const prices = priceRows
    .filter((row) => row.domain === priceDomain && row.payload && typeof row.payload === "object")
    .map((row) => {
      const snapshot = row.payload as PriceSnapshot;
      return {
        menuItemId: snapshot.menu_item_id,
        menuVariantId: snapshot.menu_variant_id ?? null,
        snapshot,
      };
    })
    .filter((row) => Boolean(row.menuItemId && row.snapshot?.ready));

  const modifierPrices = modifierPriceRows
    .filter((row) => row.domain === modifierPriceDomain && row.payload && typeof row.payload === "object")
    .map((row) => {
      const snapshot = row.payload as ModifierPriceSnapshot;
      return { modifierOptionId: snapshot.modifier_option_id, snapshot };
    })
    .filter((row) => Boolean(row.modifierOptionId && row.snapshot?.ready));

  return {
    catalogRevision: parseCatalogCursor(catalogMeta?.value),
    priceCursor: parseRuntimeCursor(priceMeta?.value),
    modifierPriceCursor: parseRuntimeCursor(modifierPriceMeta?.value),
    menus,
    prices,
    modifierPrices,
  };
}

export async function applyCatalogRuntimePayload(
  identity: TrustedTerminalIdentity,
  payload: CatalogRuntimePayload,
) {
  const scopeKey = catalogScope(identity);
  const now = new Date().toISOString();
  const eventMaxRevision = payload.events.reduce(
    (max, event) => Math.max(max, Number(event.revision) || 0),
    payload.since_revision ?? 0,
  );
  const nextRevision = payload.has_more ? eventMaxRevision : payload.latest_revision;

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL" && payload.since_revision === 0) {
      const existing = await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray();
      const keys = existing.filter((row) => row.domain === catalogDomain).map((row) => row.key);
      if (keys.length) await posRuntimeDb.projections.bulkDelete(keys);
    }

    for (const event of payload.events) {
      const key = catalogProjectionKey(identity, event.menu_item_id);
      if (event.runtime_action === "REMOVE" || !event.snapshot) {
        await posRuntimeDb.projections.delete(key);
        continue;
      }

      await posRuntimeDb.projections.put({
        key,
        scopeKey,
        domain: catalogDomain,
        revision: String(event.revision),
        payload: event.snapshot,
        updatedAt: now,
      });
    }

    await posRuntimeDb.runtimeMeta.put({
      key: catalogCursorKey(identity),
      scopeKey,
      value: { revision: nextRevision },
      updatedAt: now,
    });
  });

  return nextRevision;
}

export async function applyPriceRuntimePayload(
  identity: TrustedTerminalIdentity,
  payload: PriceRuntimePayload,
) {
  const scopeKey = priceScope(identity);
  const now = new Date().toISOString();

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL") {
      const existing = await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray();
      const keys = existing.filter((row) => row.domain === priceDomain).map((row) => row.key);
      if (keys.length) await posRuntimeDb.projections.bulkDelete(keys);
    }

    for (const event of payload.events) {
      const key = priceProjectionKey(identity, event.menu_item_id, event.menu_variant_id ?? null);
      if (event.runtime_action === "REMOVE" || !event.snapshot?.ready) {
        await posRuntimeDb.projections.delete(key);
        continue;
      }

      await posRuntimeDb.projections.put({
        key,
        scopeKey,
        domain: priceDomain,
        revision: event.snapshot.price_version_id,
        payload: event.snapshot,
        updatedAt: now,
      });
    }

    await posRuntimeDb.runtimeMeta.put({
      key: priceCursorKey(identity),
      scopeKey,
      value: payload.next_cursor,
      updatedAt: now,
    });
  });

  return payload.next_cursor;
}

export async function applyModifierPriceRuntimePayload(
  identity: TrustedTerminalIdentity,
  payload: ModifierPriceRuntimePayload,
) {
  const scopeKey = modifierPriceScope(identity);
  const now = new Date().toISOString();

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL") {
      const existing = await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray();
      const keys = existing.filter((row) => row.domain === modifierPriceDomain).map((row) => row.key);
      if (keys.length) await posRuntimeDb.projections.bulkDelete(keys);
    }

    for (const event of payload.events) {
      const key = modifierPriceProjectionKey(identity, event.modifier_option_id);
      if (event.runtime_action === "REMOVE" || !event.snapshot?.ready) {
        await posRuntimeDb.projections.delete(key);
        continue;
      }

      await posRuntimeDb.projections.put({
        key,
        scopeKey,
        domain: modifierPriceDomain,
        revision: event.snapshot.price_version_id,
        payload: event.snapshot,
        updatedAt: now,
      });
    }

    await posRuntimeDb.runtimeMeta.put({
      key: modifierPriceCursorKey(identity),
      scopeKey,
      value: payload.next_cursor,
      updatedAt: now,
    });
  });

  return payload.next_cursor;
}
